// src/hooks/drivers/useDrivers.js

import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";

export default function useDriver() {
  const { baseurl, token, accessToken, AdminId, setIsUnauthorised } =
    useGlobalContext();

  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchDrivers = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/getDriverList`,
        { userid: "", adminId: AdminId },
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        setDrivers(res.data.data || []);
        return res.data.data || [];
      }

      if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(res.data.responseMessage);
      }

      return [];
    } catch (err) {
      toast.error("Error fetching drivers");
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  }, [baseurl, token, AdminId]);

  const deleteDriver = async (driverId) => {
    try {
      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/deleteDriver`,
        { driverId, adminId: AdminId },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (res.data.responseCode === "00") {
        toast.success(res.data.responseMessage);
        setDrivers((prev) => prev.filter(d => d.driverId !== driverId));
      } else {
        toast.error(res.data.responseMessage);
      }
    } catch {
      toast.error("Error deleting driver");
    }
  };

  const toggleDutyStatus = async (driver, isOnDuty) => {
    const nextStatus = isOnDuty ? "off_duty" : "on_duty";

    setDrivers(prev =>
      prev.map(d =>
        d.driverId === driver.driverId
          ? { ...d, dutyStatus: nextStatus }
          : d
      )
    );

    try {
      const res = await axios.post(
        `${baseurl}/api/User/v1.0/updateDriverDutyTime`,
        {
          device: "Admin",
          userId: "",
          driverId: driver.driverId,
          dutyValue: nextStatus,
          adminId: AdminId,
        },
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        toast.success("Duty status updated");
        fetchDrivers(true);
      } else {
        toast.error(res.data.responseMessage);
      }
    } catch {
      toast.error("Error updating duty status");
    }
  };

  const createDriverApi = async (payload, onSuccess) => {
    try {
      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/createDriver`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        toast.success(res.data.responseMessage);
        onSuccess?.();
        fetchDrivers(true);
      } else {
        toast.error(res.data.responseMessage);
      }
    } catch {
      toast.error("Error adding driver");
    }
  };

  return {
    drivers,
    loading,
    fetchDrivers,
    deleteDriver,
    toggleDutyStatus,
    createDriverApi,
  };
}
